/**
 * Pressing into a number box selects the number in it (FB-1116).
 *
 * "When user clicks in to box to clear it out or default it to 0 so when they
 * press 2 it is 2 instead of 12." Every grid starts its boxes at something —
 * on hand, a suggestion, what was ordered — and the caret landed after it, so
 * the first key appended. Selecting the number on the way in makes the first
 * key replace it, which is what a person typing into a box of numbers means.
 *
 * One rule for the whole app rather than an attribute on each box: a box added
 * next month behaves the same without anyone remembering to ask for it. It
 * applies to boxes that hold a number — `inputMode` numeric or decimal, or
 * `type="number"` — which is every quantity, count and price box here. A
 * second press into a box already focused places the caret as usual, so a
 * number can still be edited in the middle.
 */

export interface BoxSignals {
  readonly tag: string;
  readonly type: string;
  readonly inputMode: string;
  readonly readOnly: boolean;
  readonly disabled: boolean;
}

export function selectsOnFocus(box: BoxSignals): boolean {
  if (box.tag !== 'INPUT' || box.readOnly || box.disabled) return false;
  return box.type === 'number' || box.inputMode === 'numeric' || box.inputMode === 'decimal';
}

/** Install once, at the root. Returns the uninstaller, for tests. */
export function installSelectOnFocus(root: Document = document): () => void {
  // The press that focused the box would put the caret where the mouse let
  // go and undo the selection; that one mouseup is told not to.
  let justSelected: EventTarget | null = null;
  const onFocus = (event: FocusEvent) => {
    const box = event.target;
    if (!(box instanceof HTMLInputElement)) return;
    if (
      !selectsOnFocus({
        tag: box.tagName,
        type: box.type,
        inputMode: box.inputMode,
        readOnly: box.readOnly,
        disabled: box.disabled,
      })
    ) {
      return;
    }
    box.select();
    justSelected = box;
  };
  const onMouseUp = (event: MouseEvent) => {
    if (justSelected !== null && event.target === justSelected) event.preventDefault();
    justSelected = null;
  };
  root.addEventListener('focusin', onFocus);
  root.addEventListener('mouseup', onMouseUp, true);
  return () => {
    root.removeEventListener('focusin', onFocus);
    root.removeEventListener('mouseup', onMouseUp, true);
  };
}
